import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete } from 'components/FormFields';
import { PaymentActions } from 'state/modules/payment';
import i18n from 'i18n-js';
import { FETCH_STATE } from 'constants';
import { debounce } from 'lodash';
import { usePrevious } from 'hooks';

const formatter = (option) => {
  const info = option?.card || option?.ach;
  return `${option?.payment_type} ending with ${info?.last_four}`;
};

const PaymentMethodsSelect = ({
  id,
  label,
  style,
  margin,
  variant,
  disabled,
  required,
  customParams,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  accountId,
  acceptedPaymentTypes,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const prevvalue = usePrevious(value);
  const prev = usePrevious(customParams);

  const paymentMethods = useSelector((state) => state.payment.paymentMethods);
  const fetchPaymentMethods = useSelector((state) => state.payment.fetchPaymentMethods);

  const loading = isNaN(accountId) || fetchPaymentMethods === FETCH_STATE.LOADING;
  const options = paymentMethods.map((paymentMethod) => {
    const isSupported = acceptedPaymentTypes?.includes(paymentMethod?.payment_type);
    return {
      ...paymentMethod,
      disabled: paymentMethod.disabled || !isSupported,
      isSupported: isSupported,
    };
  });

  useEffect(() => {
    if (prev && customParams && JSON.stringify(prev) !== JSON.stringify(customParams)) {
      getPaymentMethods({ ...params, ...customParams });
    }
  }, [customParams]); // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value) {
      const defaultVal = paymentMethods.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, paymentMethods]);

  const getPaymentMethodsDebounced = useCallback(
    debounce((params) => {
      getPaymentMethods(params);
    }),
    []
  );

  const getPaymentMethods = useCallback(
    (params) => {
      const payload = {
        ...params,
        account_id: accountId,
      };
      if (accountId) {
        dispatch(PaymentActions.fetchPaymentMethods(payload));
      }
    },
    [accountId, dispatch]
  );

  // Fetch payment methods on load once an account id is available (from user or organization)
  useEffect(() => {
    if (accountId) {
      getPaymentMethods(params);
    }
  }, [accountId]); // eslint-disable-line

  const handleGetPaymentMethods = () => {
    if (options.length === 0 && !loading && !params?.keyword) getPaymentMethods(params);
  };

  return (
    <Autocomplete
      label={label || i18n.t('common.payment_method')}
      id={id}
      name={name}
      style={style}
      margin={margin}
      fullwidth="true"
      variant={variant}
      loading={loading}
      disabled={disabled}
      onHighlightChange={handleGetPaymentMethods}
      clearOnBlur={false}
      value={defaultValue}
      options={options}
      getOptionDisabled={(option) => option?.disabled}
      getOptionLabel={(option) => formatter(option)}
      onChange={(_, val) => setFieldValue(name, val?.id || '')}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value.id}
      onInputChange={(_, val) => getPaymentMethodsDebounced({ ...params })}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      dataCy="payment-method-selector"
      required={required}
    />
  );
};

export { PaymentMethodsSelect };
